/**
 * Population Health — entity-layer hooks.
 *
 * Thin wrappers over the existing `populationHealthSlice` + its thunks,
 * so the new `pages/population-health` never imports `redux_store/...`
 * directly. Keeps the data-fetching logic (including the mode-specific
 * endpoint split) in one place and gives the page a clean read/write
 * surface.
 *
 * Nothing here changes Redux shape — this is a reorganisation, not a
 * migration. The legacy slice keeps running the show.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  REQUEST_STATUS,
  fetchPopulationMetrics,
  fetchBaselineFollowupMetrics,
  fetchSurveysData,
  setAssessmentInstrument,
  setComparisonMode,
  setDateRange,
  setPopulationHealthAccount,
  setSelectedMetric,
  canselAllFilters,
} from 'redux_store';

// Format a Date as YYYY-MM-DD in local timezone — the backend accepts
// ISO dates but is timezone-naive on this endpoint; matching the
// legacy behaviour avoids off-by-one drift near midnight UTC.
const formatLocalDate = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Filter state + setters. Filters live in Redux because the chart,
 * the filter bar, and the export action all read them.
 */
export const usePopulationHealthFilters = () => {
  const dispatch = useDispatch();
  const {
    dateRange,
    selectedAccount,
    selectedInstrument,
    isComparisonMode,
  } = useSelector((state) => state.populationHealth);

  const updateDateRange = useCallback(
    (range) => dispatch(setDateRange(range)),
    [dispatch]
  );
  const updateAccount = useCallback(
    (account) => dispatch(setPopulationHealthAccount(account)),
    [dispatch]
  );
  const updateInstrument = useCallback(
    (instrument) => {
      const value =
        instrument && typeof instrument === 'object' ? instrument.id : instrument;
      dispatch(setAssessmentInstrument(value));
    },
    [dispatch]
  );
  const updateComparisonMode = useCallback(
    (next) => dispatch(setComparisonMode(!!next)),
    [dispatch]
  );
  const clearAll = useCallback(() => dispatch(canselAllFilters()), [dispatch]);

  return {
    dateRange,
    selectedAccount,
    selectedInstrument,
    isComparisonMode,
    setDateRange: updateDateRange,
    setAccount: updateAccount,
    setInstrument: updateInstrument,
    setComparisonMode: updateComparisonMode,
    clearAll,
  };
};

/**
 * Metrics loader. Re-fetches whenever the filters or mode change.
 * Returns the raw slice data plus derived flags for the common states.
 */
export const usePopulationHealthMetrics = (currentAccountUuid) => {
  const dispatch = useDispatch();
  const {
    metrics,
    totalPatients,
    totalAlerts,
    totalBaselinePatients,
    totalFollowupPatients,
    totalBaselineAlerts,
    totalFollowupAlerts,
    status,
    error,
    dateRange,
    selectedAccount,
    selectedInstrument,
    isComparisonMode,
  } = useSelector((state) => state.populationHealth);

  const accountUuid = selectedAccount?.uuid || currentAccountUuid;

  useEffect(() => {
    if (!accountUuid) return;
    let filters = {
      assessmentType:
        selectedInstrument === 'all' ? undefined : selectedInstrument,
    };
    if (dateRange?.startDate && dateRange?.endDate) {
      filters = {
        ...filters,
        startDate: formatLocalDate(dateRange.startDate),
        endDate: formatLocalDate(dateRange.endDate),
      };
    }
    if (isComparisonMode) {
      dispatch(fetchBaselineFollowupMetrics({ accountUuid, filters }));
    } else {
      dispatch(fetchPopulationMetrics({ accountUuid, filters }));
    }
  }, [dispatch, accountUuid, selectedInstrument, dateRange, isComparisonMode]);

  // Drop any in-flight filter state when the page unmounts — matches
  // the legacy `canselAllFilters` cleanup so remounting doesn't show
  // stale metrics from another account.
  useEffect(() => () => dispatch(canselAllFilters()), [dispatch]);

  return useMemo(
    () => ({
      metrics,
      totalPatients,
      totalAlerts,
      totalBaselinePatients,
      totalFollowupPatients,
      totalBaselineAlerts,
      totalFollowupAlerts,
      status,
      error,
      isLoading: status === REQUEST_STATUS.loading,
      isFailed: status === REQUEST_STATUS.failed,
      isEmpty:
        status === REQUEST_STATUS.succeeded &&
        Array.isArray(metrics) &&
        metrics.length === 0,
    }),
    [
      metrics,
      totalPatients,
      totalAlerts,
      totalBaselinePatients,
      totalFollowupPatients,
      totalBaselineAlerts,
      totalFollowupAlerts,
      status,
      error,
    ]
  );
};

/**
 * Assessment-instrument catalogue. Re-fetches when the selected
 * account changes (DS switches between child accounts).
 */
export const usePopulationHealthInstruments = (currentAccountUuid) => {
  const dispatch = useDispatch();
  const { assessmentInstruments, surveysStatus, selectedAccount } = useSelector(
    (state) => state.populationHealth
  );
  const accountUuid = selectedAccount?.uuid || currentAccountUuid;
  const lastLoadedRef = useRef(null);

  useEffect(() => {
    if (!accountUuid) return;
    if (lastLoadedRef.current === accountUuid) return;
    lastLoadedRef.current = accountUuid;
    dispatch(fetchSurveysData({ accountUuid }));
  }, [dispatch, accountUuid]);

  return {
    instruments: assessmentInstruments,
    status: surveysStatus,
    isLoading: surveysStatus === REQUEST_STATUS.loading,
  };
};

/**
 * Selected metric (bar the user clicked on). Separate hook so the
 * drilldown modal can own its open/closed state without touching the
 * rest of the page.
 */
export const useSelectedMetric = () => {
  const dispatch = useDispatch();
  const selectedMetric = useSelector(
    (state) => state.populationHealth.selectedMetric
  );
  const setMetric = useCallback(
    (metric) => dispatch(setSelectedMetric(metric)),
    [dispatch]
  );
  const clear = useCallback(() => dispatch(setSelectedMetric(null)), [dispatch]);
  return { selectedMetric, setMetric, clear };
};
